import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { Modal, Button, Select, MenuItem, CircularProgress, Checkbox } from "@mui/material";
import TextField from "@mui/material/TextField";
import CloseIcon from "@mui/icons-material/Close";
import {
  useGetCategoriesQuery, useUpdateColorMutation, useAddColorMutation
} from "../service/features/api";
import { VEHICLE_TYPE } from "../constants/enum";
import { getVehicleTypeLabel } from "../utils/vehicle";
import { toast } from "react-toastify";


interface ModalProps {
  open: boolean;
  onClose: () => void;
  setOpen: Dispatch<SetStateAction<boolean>>;
  colorData?: any;
}

export default function AddColor({
  open,
  onClose,
  setOpen,
  colorData
}: ModalProps) {
  const [name, setName] = useState<string>("");
  const [color, setColor] = useState<string>("#000000");
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedVehicleType, setSelectedVehicleType] = useState<number | "">("");

  const [errors, setErrors] = useState<any>({});

  const { data: categoriesData } = useGetCategoriesQuery(
    { search: "", page: 1, limit: 100, type: selectedVehicleType },
    { skip: !selectedVehicleType }
  );
  const [addColor, { isLoading: isAdding }] = useAddColorMutation();
  const [updateColor, { isLoading: isUpdating }] = useUpdateColorMutation();

  useEffect(() => {
    if (colorData && open) {
      setName(colorData.name || "");
      setColor(colorData.colorCode || colorData.name || "#000000"); // fallback

      let catIds: string[] = [];
      if (colorData.categories && Array.isArray(colorData.categories)) {
        catIds = colorData.categories.map((c: any) => c._id);
      } else if (colorData.categoryId) {
        catIds = Array.isArray(colorData.categoryId) ? colorData.categoryId : [colorData.categoryId];
      }
      setSelectedCategories(catIds);
      setSelectedCategories(catIds);
      const vType = colorData.type;
      setSelectedVehicleType(Array.isArray(vType) ? vType[0] : (vType || ""));
      setErrors({});
    } else if (!colorData && open) {
      setName("");
      setColor("#000000");
      setSelectedCategories([]);
      setSelectedVehicleType("");
      setErrors({});
    }
  }, [colorData, open]);

  const handleCategoryChange = (event: any) => {
    const value = event.target.value;
    const categories = categoriesData?.data?.data || [];

    if (value.includes("all")) {
      if (selectedCategories.length === categories.length) {
        setSelectedCategories([]);
      } else {
        setSelectedCategories(categories.map((cat: any) => cat._id));
      }
    } else {
      setSelectedCategories(typeof value === 'string' ? (value as string).split(',') : value);
    }
    if (errors.category) setErrors({ ...errors, category: "" });
  };

  const handleVehicleTypeChange = (event: any) => {
    const value = event.target.value as number;
    setSelectedVehicleType(value);
    setSelectedCategories([]); // Reset categories when vehicle type changes
    if (errors.vehicleType) setErrors({ ...errors, vehicleType: "" });
  };

  const validate = () => {
    let tempErrors: any = {};
    let isValid = true;

    if (!name.trim()) {
      tempErrors.name = "Color Name is required / اسم اللون مطلوب";
      isValid = false;
    }

    if (selectedCategories.length === 0) {
      tempErrors.category = "At least one category is required / مطلوب فئة واحدة على الأقل";
      isValid = false;
    }
    if (!selectedVehicleType) {
      tempErrors.vehicleType = "Vehicle type is required / نوع المركبة مطلوب";
      isValid = false;
    }
    // Color picker always returns a value, so strictly not needed unless custom logic

    setErrors(tempErrors);
    return isValid;
  };

  const handleSave = async () => {
    if (!validate()) return;

    const payload = {
      name,
      type: Number(selectedVehicleType),
      categoryId: selectedCategories,
    };

    try {
      if (colorData?._id) {
        await updateColor({ id: colorData._id, ...payload }).unwrap();
        toast.success("Color updated successfully / تم تحديث اللون بنجاح");
      } else {
        await addColor(payload).unwrap();
        toast.success("Color added successfully / تمت إضافة اللون بنجاح");
      }
      setOpen(false);
      onClose();
    } catch (err: any) {
      toast.error(err?.data?.message || "Operation failed / فشلت العملية");
    }
  };

  return (
    <Modal
      className="modal rejectReason_modal"
      open={open}
      onClose={onClose}
    >
      <div className="modal-dialog">
        <div className="modal-body">
          <div className="modal_title">
            <h2>{colorData ? "Edit Color / تعديل اللون" : "Add Color / إضافة لون"}</h2>
            <button
              type="button"
              className="btn-close"
              onClick={() => { setOpen(false); onClose(); }}
              aria-label="Close / إغلاق"
            >
              <CloseIcon />
            </button>
          </div>

          <div className="form">

            <div className="gap_p">


              <div className="control_group w_50">
                <label htmlFor="color-name">Color Name / اسم اللون</label>
                <TextField
                  id="color-name"
                  placeholder="Enter Name / أدخل الاسم"
                  fullWidth
                  hiddenLabel
                  value={name}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value.length <= 40) {
                      setName(value);
                      if (errors.name) setErrors({ ...errors, name: "" });
                    }
                  }}
                  error={!!errors.name}
                  helperText={errors.name}
                  inputProps={{ maxLength: 40 }}
                />
              </div>

              <div className="control_group">
                <label htmlFor="color-picker">Color Picker / منتقي الألوان</label>
                <input
                  type="color"
                  id="color-picker"
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  style={{ width: '100%', height: '40px' }}
                />
              </div>

              <div className="control_group">
                <label htmlFor="VehicleType">Vehicle Type / نوع المركبة</label>
                <Select
                  fullWidth
                  hiddenLabel
                  displayEmpty
                  value={selectedVehicleType}
                  onChange={handleVehicleTypeChange}
                  renderValue={(selected) => {
                    if (!selected) return "Select Vehicle Type / اختر نوع المركبة";
                    return getVehicleTypeLabel(selected);
                  }}
                  error={!!errors.vehicleType}
                >
                  <MenuItem disabled value="">
                    <em>Select Vehicle Type / اختر نوع المركبة</em>
                  </MenuItem>
                  {Object.keys(VEHICLE_TYPE)
                    .filter(k => isNaN(Number(k)))
                    .map((key) => (
                      <MenuItem key={key} value={(VEHICLE_TYPE as any)[key]}>
                        {getVehicleTypeLabel((VEHICLE_TYPE as any)[key])}
                      </MenuItem>
                    ))}
                </Select>
                {errors.vehicleType && <div className="error_text" style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>{errors.vehicleType}</div>}
              </div>

              <div className="control_group">
                <label htmlFor="Category">Category / الفئة</label>
                <Select
                  multiple
                  fullWidth
                  hiddenLabel
                  value={selectedCategories}
                  onChange={handleCategoryChange}
                  renderValue={(selected) => {
                    const selectedIds = (selected as string[]) || [];
                    if (selectedIds.length === 0) return selectedVehicleType ? "Select Categories / اختر الفئات" : "Select Vehicle Type First / اختر نوع المركبة أولاً";

                    return selectedIds.map(id => {
                      const cat = categoriesData?.data?.data?.find((c: any) => c._id === id);
                      if (cat?.name) return cat.name;

                      // Fallback for edit mode if category not in current page
                      const source = colorData?.categories || colorData?.categoryId;
                      const items = Array.isArray(source) ? source : [source];
                      const found = items.find((c: any) => c && typeof c === 'object' && (c._id === id || c.id === id));
                      if (found?.name) return found.name;

                      return id;
                    }).join(", ");
                  }}
                  disabled={!selectedVehicleType}
                  error={!!errors.category}
                >
                  <MenuItem value="" disabled>
                    {selectedVehicleType ? "Select Categories / اختر الفئات" : "Select Vehicle Type First / اختر نوع المركبة أولاً"}
                  </MenuItem>
                  {categoriesData?.data?.data?.length > 0 && (
                    <MenuItem value="all">
                      <em>{selectedCategories.length === categoriesData.data.data.length ? "Deselect All / إلغاء تحديد الكل" : "Select All / تحديد الكل"}</em>
                    </MenuItem>
                  )}
                  {categoriesData?.data?.data?.map((cat: any) => (
                    <MenuItem key={cat._id} value={cat._id}>
                      <Checkbox checked={selectedCategories.includes(cat._id)} />
                      {cat.name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.category && <div className="error_text" style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>{errors.category}</div>}
              </div>
            </div>
            <div className="footer_btn_flex">
              <Button
                className="btn btn_primary"
                onClick={handleSave}
                disabled={isAdding || isUpdating}
              >
                {isAdding || isUpdating ? <CircularProgress size={20} color="inherit" /> : "Save / حفظ"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}
